/**
 * Bande « Demain et plus loin » du catalogue.
 *
 * Le catalogue s'ouvre sur ce qui passe aujourd'hui et ce qui est déjà passé ;
 * les fiches prévues après la journée d'antenne en cours attendent derrière une
 * bande repliée, en tête de liste. Dépliées, elles s'affichent au-dessus de la
 * bande, qui reste à sa place de séparateur et défile avec le reste.
 */

import type { Program } from '../../types/index.js'

export type UpcomingSortKey = 'title' | 'durationSec' | 'createdAt' | 'premiereDate' | 'type'

/**
 * Comment charger et afficher le groupe « à venir ».
 *
 * Trié par date de première diffusion, le groupe est lu du plus proche au plus
 * lointain puis affiché **retourné** : demain se retrouve collé à la bande, les
 * dates lointaines en haut. Sans ce retournement, un tri décroissant chargerait
 * d'abord les dates les plus lointaines et laisserait demain — le seul jour qui
 * appelle une action — sur une page jamais demandée.
 *
 * Les autres tris (titre, durée…) n'ont pas de « proche de la bande » : le
 * groupe suit simplement le tri de la liste.
 */
export function upcomingOrdering(sortBy: UpcomingSortKey, sortDir: 'asc' | 'desc'): {
  fetchSortDir: 'asc' | 'desc'
  reversed:     boolean
} {
  if (sortBy === 'premiereDate') return { fetchSortDir: 'asc', reversed: true }
  return { fetchSortDir: sortDir, reversed: false }
}

/** Lignes du groupe « à venir » dans leur ordre d'affichage. */
export function upcomingDisplayRows(programs: Program[], reversed: boolean): Program[] {
  return reversed ? [...programs].reverse() : programs
}

/**
 * Identifiants de toutes les lignes visibles, dans l'ordre de l'écran : ceux
 * du groupe « à venir » quand il est déplié, puis la liste principale. La
 * sélection par plage (Maj+clic) et « tout cocher » s'appuient dessus : une
 * fiche repliée ne doit jamais être cochée sans être vue.
 */
export function visibleCatalogIds(current: Program[], upcomingRows: Program[], expanded: boolean): string[] {
  const ids = current.map(program => program.id)
  if (!expanded) return ids
  // Une fiche redatée entre deux chargements peut transiter par les deux
  // listes : elle n'apparaît qu'une fois, là où elle est désormais.
  const upcomingIds = upcomingRows.map(program => program.id)
  const seen = new Set(upcomingIds)
  return [...upcomingIds, ...ids.filter(id => !seen.has(id))]
}
